import type { FC, ReactNode } from 'react';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { SECURITY_DEFAULT_DATE_RANGE } from 'src/mindtime/constants/securityFilters';
import { useIsResellerOrg } from 'src/mindtime/hooks/useIsResellerOrg';
import { fetchWazuhClientCompanies } from 'src/mindtime/pages/security/edr/shared/wazuhClientCompanies';
import {
  setSecurityDateRange,
  setSecuritySelectedClientId,
  setSecuritySelectedEndpointId,
} from 'src/store/slices/securityFiltersSlice';
import type { WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';

export type WazuhClientOrganization = {
  id: string;
  name: string;
};

export const WAZUH_DATE_RANGE_OPTIONS = ['Last 7 days', 'Last 30 days'] as const;

export type WazuhDateRangeOption = (typeof WAZUH_DATE_RANGE_OPTIONS)[number] | '';

const isWazuhDateRangeOption = (value: string): value is (typeof WAZUH_DATE_RANGE_OPTIONS)[number] =>
  (WAZUH_DATE_RANGE_OPTIONS as readonly string[]).includes(value);

const computeRangeBounds = (
  option: WazuhDateRangeOption
): { startDate?: string; endDate?: string } => {
  const days = option === 'Last 7 days' ? 7 : option === 'Last 30 days' ? 30 : 0;
  if (!days) return {};
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { startDate: start.toISOString(), endDate: now.toISOString() };
};

type WazuhScopeContextValue = {
  isReseller: boolean;
  resellerId: string | undefined;
  organizationId: string | undefined;
  selectedClientId: string | null;
  setSelectedClientId: (clientId: string | null) => void;
  /** Global EDR Endpoint filter (comma-separated wazuhAgentId values). */
  selectedEndpointId: string | null;
  setSelectedEndpointId: (endpointId: string | null) => void;
  dateRange: WazuhDateRangeOption;
  setDateRange: (range: WazuhDateRangeOption) => void;
  scope: WazuhScopeParams;
  /** Logged-in user's display name (profile.name → firstName lastName → 'User'). */
  userDisplayName: string;
  /** Logged-in user's organization name (profile.organization.name). */
  resellerName: string;
  getOrganizationName: (orgId: string) => string | undefined;
};

const WazuhScopeContext = createContext<WazuhScopeContextValue | null>(null);

export const WazuhScopeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { userData } = useUserProfile();
  const dispatch = useAppDispatch();
  const storedDateRange = useAppSelector((state) => state.securityFilters.global.dateRange);
  const dateRange: WazuhDateRangeOption = isWazuhDateRangeOption(storedDateRange)
    ? storedDateRange
    : SECURITY_DEFAULT_DATE_RANGE;
  const selectedClientId = useAppSelector((state) => state.securityFilters.global.selectedClientId);
  const selectedEndpointId = useAppSelector(
    (state) => state.securityFilters.global.selectedEndpointId
  );
  const [clientOrganizations, setClientOrganizations] = useState<WazuhClientOrganization[]>([]);

  const setDateRange = useCallback(
    (range: WazuhDateRangeOption) => {
      dispatch(
        setSecurityDateRange(isWazuhDateRangeOption(range) ? range : SECURITY_DEFAULT_DATE_RANGE)
      );
    },
    [dispatch]
  );

  const setSelectedClientId = useCallback(
    (clientId: string | null) => {
      dispatch(setSecuritySelectedClientId(clientId));
    },
    [dispatch]
  );

  const setSelectedEndpointId = useCallback(
    (endpointId: string | null) => {
      dispatch(setSecuritySelectedEndpointId(endpointId));
    },
    [dispatch]
  );

  const profileOrg = userData?.organization;
  const isReseller = useIsResellerOrg();
  const resellerId = isReseller ? profileOrg?.id : undefined;
  const resellerName = profileOrg?.name ?? 'User';

  const userDisplayName = useMemo(() => {
    const trimmed = userData?.name?.trim();
    if (trimmed) return trimmed;
    const fullName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim();
    return fullName || 'User';
  }, [userData?.name, userData?.firstName, userData?.lastName]);

  const organizationId = useMemo(() => {
    if (isReseller) {
      return selectedClientId ?? undefined;
    }
    return profileOrg?.id;
  }, [isReseller, profileOrg?.id, selectedClientId]);

  const scope = useMemo<WazuhScopeParams>(() => {
    const range = computeRangeBounds(dateRange);
    if (isReseller && resellerId) {
      return {
        resellerId,
        ...(selectedClientId ? { organizationId: selectedClientId } : {}),
        ...range,
      };
    }
    if (profileOrg?.id) {
      return { organizationId: profileOrg.id, ...range };
    }
    return { ...range };
  }, [isReseller, resellerId, selectedClientId, profileOrg?.id, dateRange]);

  // Preload a small list of clients so name lookups work without a roundtrip
  useEffect(() => {
    if (!isReseller || !resellerId) {
      setClientOrganizations([]);
      return undefined;
    }

    let cancelled = false;
    fetchWazuhClientCompanies(resellerId, '', 0, 100)
      .then((normalized) => {
        if (!cancelled) setClientOrganizations(normalized);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'WazuhScopeProvider');
          setClientOrganizations([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isReseller, resellerId]);

  const getOrganizationName = useCallback(
    (orgId: string): string | undefined => {
      const fromClients = clientOrganizations.find((org) => org.id === orgId)?.name;
      if (fromClients) return fromClients;
      if (profileOrg?.id === orgId) return profileOrg.name;
      return undefined;
    },
    [clientOrganizations, profileOrg?.id, profileOrg?.name]
  );

  const value = useMemo(
    () => ({
      isReseller,
      resellerId,
      organizationId,
      selectedClientId,
      setSelectedClientId,
      selectedEndpointId,
      setSelectedEndpointId,
      dateRange,
      setDateRange,
      scope,
      userDisplayName,
      resellerName,
      getOrganizationName,
    }),
    [
      isReseller,
      resellerId,
      organizationId,
      selectedClientId,
      setSelectedClientId,
      selectedEndpointId,
      setSelectedEndpointId,
      dateRange,
      setDateRange,
      scope,
      userDisplayName,
      resellerName,
      getOrganizationName,
    ]
  );

  return createElement(WazuhScopeContext.Provider, { value }, children);
};

export const useWazuhScope = (): WazuhScopeContextValue => {
  const context = useContext(WazuhScopeContext);
  if (!context) {
    throw new Error('useWazuhScope must be used within WazuhScopeProvider');
  }
  return context;
};
